import { useEditApiKey } from 'api/ApiKeyApi';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body2, body2Bold } from 'ui/theme';
import { useModalContext } from 'utils/contexts/modal';
import { fetchApiKeysRequest } from '../actions';
import { formulateEditApiKeyPayload } from '../utils';
import { MODAL_ACTION, ModalState } from './ApiKeyModal.constants';

const Strong = styled.span`
  ${body2Bold}
`;

export const DISABLE_API_KEY_MODAL_TEXT = {
  errorText: 'Unable to disable API key.',
  header: 'Disable API key?',
  modalId: 'disable-api-key-modal',
};

export const DisableApiKeyModal = () => {
  const dispatch = useDispatch();
  const { hideModal, modalState } = useModalContext<ModalState>();

  const { mutate: editApiKey } = useEditApiKey({
    onSuccess: () => {
      dispatch(fetchApiKeysRequest());
      dispatch(addPositiveToast('Saved changes.'));
      hideModal();
    },
    onError: () => {
      dispatch(addNegativeToast('Unable to update API key.'));
    },
  });

  if (!modalState?.apiKey) {
    return null;
  }

  const { action, apiKey } = modalState;

  const onSubmit = () => {
    const payload = formulateEditApiKeyPayload(
      { ...apiKey },
      { enabled: false, keyHash: apiKey.keyHash },
    );
    editApiKey(payload);
  };

  return (
    <Modal
      id={DISABLE_API_KEY_MODAL_TEXT.modalId}
      isModalOpen={action === MODAL_ACTION.DISABLE_API_KEY}
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>{DISABLE_API_KEY_MODAL_TEXT.header}</ModalHeader>
      <ModalBody>
        <Body2>
          Are you sure you want to disable{' '}
          {apiKey.name ? <Strong>{apiKey.name}</Strong> : 'this API key'}? It will no longer work,
          but can be re-enabled later.
        </Body2>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <DestructiveButton
          id="disable-api-key-modal-confirm-btn"
          onClick={onSubmit}
          size={BUTTON_SIZES.LARGE}
        >
          Disable API key
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
